import {
  CircularProgress,
  IconButton,
  makeStyles,
  Typography,
  Link as MaterialLink,
} from '@material-ui/core';
import PollIcon from '@material-ui/icons/Poll';
import cn from 'classnames';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { Mentor } from '../../api/mentors';
import { UserContext } from '../../contexts/user-context';
import { getRoutePath, Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { fullName } from '../../utils/String';
import { getColorStatus } from '../../utils/functions';
import { Avatar, Link, StatusBadge, Text } from '../common';
import MentorsRowActions from '../mentors/mentors-row-actions';

const useStyles = makeStyles(() => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 20,
    backgroundColor: COLORS.COLOR_ADDITIONAL_WHITE,
    borderRadius: 8,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
  },
  cardTitle: {
    fontSize: 18,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',

    '& > a': {
      color: COLORS.COLOR_TEXT_BASE,
      textDecoration: 'none',

      '& > span, & > svg': {
        verticalAlign: 'middle',
      },
    },
  },
  cardContent: {
    display: 'flex',
    gap: 16,
  },
  description: {
    display: '-webkit-box',
    width: '100%',
    WebkitLineClamp: 4,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    lineHeight: '1.5',
  },
  iconsBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,

    '& > a': {
      padding: 4,
    },
  },
  actions: {
    marginLeft: 'auto',
  },
  warningIcon: {
    marginRight: 6,
    color: COLORS.COLOR_ADDITIONAL_ORANGE,
    fontSize: 22,
  },
  iconBox: {
    width: 27,
    height: 27,
    borderRadius: 4,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: COLORS.COLOR_BLUE_BASE,
    fontSize: 18,
    fontWeight: 'bold',
    color: COLORS.COLOR_ADDITIONAL_WHITE,
  },
  centerText: {
    transform: 'translateY(-1.5px)',
  },
  moreThanNine: {
    fontSize: 16,
  },
  iconButton: {
    border: 'none',

    '&:hover': {
      border: 'none',
    },
  },
}));

function MentorCard({
  mentor,
  cardId,
  logo,
  pathname,
  actions,
}: {
  mentor: Mentor;
  cardId: string;
  logo?: string | null;
  pathname?: string;
  actions?: React.ReactNode;
}) {
  const classes = useStyles();
  const location = useLocation();
  const { hasAccessToAction } = useContext(UserContext);

  const prevPath = `${location.pathname}${location.search}`;

  return (
    <div className={classes.card} id={cardId}>
      <div className={classes.cardHeader}>
        <Typography variant='h5' className={classes.cardTitle}>
          <Link
            to={{
              pathname:
                pathname ||
                getRoutePath(Pages.MENTOR_DETAILS, {
                  mentorId: mentor.id,
                }),
              state: { prevPath },
            }}>
            <span>{fullName(mentor.firstName, mentor.lastName)}</span>
          </Link>
        </Typography>
        <div>
          <StatusBadge
            status={mentor.status}
            variant={getColorStatus(mentor.status)}
          />
        </div>
      </div>
      <div className={classes.cardContent}>
        <Avatar
          src={logo}
          name={`${mentor.firstName[0]}${mentor.lastName[0]}`}
          size='80'>
          {typeof logo === 'string' && !logo ? (
            <CircularProgress size={18} color='inherit' />
          ) : undefined}
        </Avatar>

        {!!mentor.about && (
          <Text variant='normal' className={classes.description}>
            {mentor.about}
          </Text>
        )}
      </div>
      <div className={classes.iconsBlock}>
        {hasAccessToAction('mentor.assignemnts.view') && (
          <IconButton
            data-testid='mentor-card-assignments-icon'
            component={Link}
            to={{
              pathname: getRoutePath(Pages.MENTOR_ASSIGNMENTS, {
                mentorId: mentor.id,
              }),
              state: {
                prevPath,
              },
            }}>
            <div
              className={cn(classes.iconBox, {
                [classes.moreThanNine]: mentor.assigned > 9,
              })}>
              {!mentor.assigned
                ? '0'
                : mentor.assigned > 9
                ? '9+'
                : mentor.assigned}
            </div>
          </IconButton>
        )}
        {hasAccessToAction('mentor.statistics') && (
          <IconButton
            data-testid='mentor-card-statistics-icon'
            component={Link}
            to={{
              pathname: getRoutePath(Pages.MENTOR_STATS, {
                mentorId: mentor.id,
              }),
              state: {
                prevPath,
              },
            }}>
            <PollIcon
              fontSize='large'
              style={{ color: COLORS.COLOR_BLUE_BASE }}
            />
          </IconButton>
        )}
        {mentor.email && (
          <IconButton
            data-testid='mentor-card-email-icon'
            className={classes.iconButton}
            component={MaterialLink}
            href={`mailto:${mentor.email}`}>
            <div className={classes.iconBox}>
              <div className={classes.centerText}>@</div>
            </div>
          </IconButton>
        )}
        <div className={classes.actions}>
          {actions || <MentorsRowActions mentor={mentor} variant='list' />}
        </div>
      </div>
    </div>
  );
}

export default MentorCard;
